import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Code, FileDown } from "lucide-react";
import { Button } from "../ui/button";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
  viewport: { once: true },
};

interface LetterFields {
  recipientName: string;
  recipientAddress: string;
  date: string;
  subject: string;
  body: string;
  signerName: string;
  signerTitle: string;
}

const defaultFields: LetterFields = {
  recipientName: "Jane Smith, Acme Properties Ltd.",
  recipientAddress: "10 Downing Street,\nLondon,\nSW1A 2AA\nUnited Kingdom",
  date: "13 April 2026",
  subject: "Partnership Proposal — Pod Placement at Acme HQ",
  body: `Dear Jane,

I am writing on behalf of QWIP LTD, a private limited company incorporated in England and Wales, to propose a partnership for the placement of qwip pods at your premises.

We believe your location would be an excellent fit for our soundproof, bookable workspace pods. We would love to discuss the opportunity further at your convenience.

Please do not hesitate to contact me should you require any further information or documentation.`,
  signerName: "Your Name",
  signerTitle: "Your Title",
};

const buildLetterHtml = (f: LetterFields) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'DM Sans', Arial, sans-serif; color: #1f3d32; background: #fff; }
    .page { max-width: 700px; margin: 0 auto; padding: 52px 56px 0; min-height: 960px; position: relative; }
    .logo { font-family: 'Funnel Display', sans-serif; font-size: 36px; font-weight: 700; color: #1f3d32; margin-bottom: 24px; }
    .rule { height: 1.5px; background: #1f3d32; margin-bottom: 32px; }
    .addresses { display: flex; justify-content: space-between; margin-bottom: 36px; }
    .addr-left { font-size: 13px; line-height: 1.6; color: #1f3d32; }
    .addr-left strong { font-weight: 700; }
    .addr-right { text-align: right; font-size: 13px; line-height: 1.6; color: #1f3d32; font-weight: 700; }
    .addr-right span { font-weight: 400; display: block; }
    .date { font-size: 13px; margin-bottom: 24px; color: #1f3d32; }
    .subject { font-size: 14px; font-weight: 700; margin-bottom: 24px; color: #1f3d32; }
    .body p { font-size: 13.5px; line-height: 1.7; margin-bottom: 16px; text-align: justify; color: #1f3d32; }
    .signature { margin-top: 32px; }
    .signature .name { font-size: 14px; font-weight: 700; }
    .signature .title { font-size: 13px; color: #4a6b5c; }
    .signature .company { font-size: 13px; color: #4a6b5c; }
    .footer-bar { position: absolute; bottom: 0; left: 0; right: 0; height: 32px; background: #1f3d32; border-radius: 0 0 0 0; }
  </style>
</head>
<body>
  <div class="page">
    <div class="logo">qwip</div>
    <div class="rule"></div>
    <div class="addresses">
      <div class="addr-left">
        <strong>${f.recipientName}</strong><br/>
        ${f.recipientAddress.replace(/\n/g, "<br/>")}
      </div>
      <div class="addr-right">
        <strong>QWIP LTD</strong>
        <span>128 City Road,</span>
        <span>Liverpool Street</span>
        <span>EC1V 2NX, London</span>
        <span>United Kingdom</span>
      </div>
    </div>
    <div class="date">${f.date}</div>
    <div class="subject">Re: ${f.subject}</div>
    ${f.body.split("\n\n").map(p => `<div class="body"><p>${p}</p></div>`).join("")}
    <div class="signature">
      <p class="name">${f.signerName}</p>
      <p class="title">${f.signerTitle}</p>
      <p class="company">QWIP LTD</p>
    </div>
    <div class="footer-bar"></div>
  </div>
</body>
</html>`;

const generateDocx = async (f: LetterFields) => {
  const { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, TabStopType, TabStopPosition } = await import("docx");
  const { saveAs } = await import("file-saver");

  const bodyParagraphs = f.body.split("\n\n").map(
    (p) =>
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 240 },
        children: [new TextRun({ text: p, font: "DM Sans", size: 23 })],
      })
  );

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: { width: 11906, height: 16838 },
            margin: { top: 1200, right: 1200, bottom: 1400, left: 1200 },
          },
        },
        children: [
          // Logo
          new Paragraph({
            spacing: { after: 360 },
            children: [
              new TextRun({ text: "qwip", font: "Arial", size: 56, bold: true, color: "1f3d32" }),
            ],
          }),
          // Rule
          new Paragraph({
            spacing: { after: 400 },
            border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "1f3d32", space: 1 } },
            children: [],
          }),
          // Addresses - two column via tab
          new Paragraph({
            tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
            spacing: { after: 60 },
            children: [
              new TextRun({ text: f.recipientName, font: "DM Sans", size: 22, bold: true }),
              new TextRun({ text: "\t", font: "DM Sans" }),
              new TextRun({ text: "QWIP LTD", font: "DM Sans", size: 22, bold: true }),
            ],
          }),
          ...f.recipientAddress.split("\n").map(
            (line, i) => {
              const rightLines = ["128 City Road,", "Liverpool Street", "EC1V 2NX, London", "United Kingdom"];
              return new Paragraph({
                tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
                spacing: { after: 40 },
                children: [
                  new TextRun({ text: line, font: "DM Sans", size: 22 }),
                  new TextRun({ text: "\t", font: "DM Sans" }),
                  new TextRun({ text: rightLines[i] || "", font: "DM Sans", size: 22 }),
                ],
              });
            }
          ),
          // Date
          new Paragraph({
            spacing: { before: 400, after: 320 },
            children: [new TextRun({ text: f.date, font: "DM Sans", size: 22 })],
          }),
          // Subject
          new Paragraph({
            spacing: { after: 320 },
            children: [
              new TextRun({ text: `Re: ${f.subject}`, font: "DM Sans", size: 23, bold: true }),
            ],
          }),
          // Body
          ...bodyParagraphs,
          // Signature
          new Paragraph({
            spacing: { before: 400, after: 60 },
            children: [new TextRun({ text: f.signerName, font: "DM Sans", size: 23, bold: true })],
          }),
          new Paragraph({
            spacing: { after: 40 },
            children: [new TextRun({ text: f.signerTitle, font: "DM Sans", size: 22, color: "4a6b5c" })],
          }),
          new Paragraph({
            children: [new TextRun({ text: "QWIP LTD", font: "DM Sans", size: 22, color: "4a6b5c" })],
          }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, "qwip-letter.docx");
};

const LetterheadSection = () => {
  const [fields, setFields] = useState<LetterFields>(defaultFields);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [generating, setGenerating] = useState(false);

  const copyHtml = async () => {
    await navigator.clipboard.writeText(buildLetterHtml(fields));
    setCopiedHtml(true);
    setTimeout(() => setCopiedHtml(false), 2000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateDocx(fields);
    } finally {
      setGenerating(false);
    }
  };

  const update = (key: keyof LetterFields, val: string) =>
    setFields((prev) => ({ ...prev, [key]: val }));

  return (
    <motion.div {...fadeIn} className="mb-28">
      <h2 className="text-2xl font-semibold mb-10 text-muted-foreground">Letterhead Template</h2>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border flex-wrap gap-3">
          <p className="text-sm text-muted-foreground">Generate branded letters matching the qwip letterhead. Edit fields and download as .docx.</p>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowForm(!showForm)}>
              <Code size={14} />
              {showForm ? "Preview" : "Edit Fields"}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={copyHtml}>
              {copiedHtml ? <Check size={14} /> : <Copy size={14} />}
              {copiedHtml ? "Copied!" : "Copy HTML"}
            </Button>
            <Button variant="default" size="sm" className="gap-1.5" onClick={handleGenerate} disabled={generating}>
              <FileDown size={14} />
              {generating ? "Generating…" : "Download DOCX"}
            </Button>
          </div>
        </div>

        {showForm ? (
          <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
            <div className="grid tablet:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Recipient Name</label>
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={fields.recipientName}
                  onChange={(e) => update("recipientName", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label>
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={fields.date}
                  onChange={(e) => update("date", e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Recipient Address</label>
              <textarea
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                rows={3}
                value={fields.recipientAddress}
                onChange={(e) => update("recipientAddress", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Subject Line</label>
              <input
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                value={fields.subject}
                onChange={(e) => update("subject", e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Body (separate paragraphs with blank lines)</label>
              <textarea
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                rows={10}
                value={fields.body}
                onChange={(e) => update("body", e.target.value)}
              />
            </div>
            <div className="grid tablet:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Signer Name</label>
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={fields.signerName}
                  onChange={(e) => update("signerName", e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Signer Title</label>
                <input
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background"
                  value={fields.signerTitle}
                  onChange={(e) => update("signerTitle", e.target.value)}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 bg-muted/30">
            <div className="max-w-[700px] mx-auto border border-border rounded-xl overflow-hidden shadow-sm bg-background">
              <iframe
                srcDoc={buildLetterHtml(fields)}
                title="Letterhead preview"
                className="w-full border-0"
                style={{ height: "960px" }}
                sandbox=""
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LetterheadSection;
